import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import { ContentSummary_s } from "styles/app-styles"
import ImportIcon from "components/import-export/import-icon"
import ExportIcon from "components/import-export/export-icon"
import Import from "components/import-export/import"
import RoomTable from "components/hospitality/rooms/room-table"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import Client from "data/_client"
import api_match from "data/interface/api"
import importService from "data/import"
import inflationService, { Inflation } from "data/inflation"
import roomService, { Room } from "data/room"
import propertyService, { Property } from 'data/property'
import { alphanum } from "components/table/lib/sort"
import { localizeNum } from "data/lib/numbers"
import { ContextRow } from './room-table'
import toggles from "feature-toggles"
//Styles
import { Rooms_s } from "components/hospitality/rooms/room-styles"
import { AddIcon } from "images/icons/tools"

type SummaryType = {
  totalArea: string
  totalAreaVariance: string
  totalCalculatedArea: string
  totalCalculatedRooms: number
  totalRooms: number
  totalUnitVariance: number,
  totalFutureRooms: number,
  totalFutureArea: number
}

const tableId = "room-table"

const Rooms: FC<RouteComponentProps> = (props) => {
  const [data, setData] = useState<Room[]>([])
  const [summary, setSummary] = useState<SummaryType>()
  const [inflation, setInflation] = useState<Inflation[]>([])
  const [initSort, setInitSort] = useState<boolean>(false)
  const [showImport, toggleShowImport] = useState<boolean>(false)
  const [property, setProperty] = useState<Property|null>(null);
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const [showSelected, setShowSelected] = useState<boolean>(false)
  const [selected, setSelected] = useState<ContextRow[]>([]);

  useEffect(() => {
    importService.stream().subscribe(data => {
      toggleShowImport(data['showImport'])
    })

    toggleShowImport(false);

    const inflationSubscription = inflationService.stream().subscribe(data => {
      api_match(
        data,
        init  => setInflation(init.value),
        ok    => {
          setInflation(ok.value)
        },
        err   => {
          setInflation(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const roomSubscription = roomService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            setData([...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
            setInitSort(true)
          } else {
            setData(ok.value)
          }
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        setProperty(_.find(data.value, {propertyid: data.current}))
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current)
          break
      }
    })

    return function cleanup() {
      inflationSubscription.unsubscribe()
      roomSubscription.unsubscribe()
      propertySubscription.unsubscribe()
    }
  }, [initSort])

  useEffect(() => {
    if (_.isNil(propertyId)) return

    roomService.fetchRooms(propertyId)
    propertyService.fetchProperty(propertyId)
    inflationService.fetchInflation(propertyId)

    Client.post('msg/svc_rentroll_summary', { propertyId, _synchronous: true })
    .then((d) => _.get(d, 'data.hospitality'))
    .then(setSummary)

  }, [propertyId])

  const Add = () => {
    return (
      <a onClick={() => roomService.create(propertyService.getStoredPropertyId())}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  const [deleteFlag, setDeleteFlag] = useState(0)
  const Delete = () => {
    return (
      <WithTip position="bottom">
        <span>Delete Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput')  ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setDeleteFlag(deleteFlag+1)
        }}>
          <span className="p-menuitem-icon pi pi-fw pi-trash"></span>
        </a>
      </WithTip>
    )
  }

  const [pasteFlag, setPasteFlag] = useState(0)
  const Paste = () => {
    return (
      <WithTip position="bottom">
        <span>Paste to Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput') ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setPasteFlag(pasteFlag+1)
          }
        }>
          <span className="p-menuitem-icon pi pi-fw pi-clone"></span>
        </a>
      </WithTip>
    )
  }

  const ShowSelected = () => (
    <WithTip position="bottom">
      <span>Show {showSelected ? 'All' : 'Selected'} Rows</span>
      <a className={selected.length ? '' : 'disabled'}
        onClick={() => {
          if (!selected.length) return
          setShowSelected(!showSelected)
        }}>
        <span className={showSelected ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{height: '18px', width: '18px'}}></span>
      </a>
    </WithTip>
  )

  useEffect(() => {
    setSelected(_.intersectionBy(data, selected, 'roomid'))
  }, [data])

  useEffect(() => {
    if (showSelected && _.isEmpty(selected)) setShowSelected(false)
  }, [selected])

  const _data = showSelected ? selected : data

  let summaryText
  if (summary) {
    const f = (v) => localizeNum(v) || '-'
    const { totalArea, totalAreaVariance, totalCalculatedArea, totalRooms, totalCalculatedRooms, totalUnitVariance, totalFutureRooms, totalFutureArea } = summary
    summaryText = `Area: Total: ${f(totalArea)} | Input: ${f(totalCalculatedArea)} | Variance: ${f(totalAreaVariance)} | Future Area: ${f(totalFutureArea)} — Rooms: Total: ${f(totalRooms)} | Input: ${f(totalCalculatedRooms)} | Variance: ${f(totalUnitVariance)} | Future Rooms: ${f(totalFutureRooms)}`
  }

  return (
    <Rooms_s>
      <Toolbar>
        <Add />
        {toggles.multiselect() && <>
          <Paste />
          <Delete />
          <ShowSelected />
        </>}
        <ImportIcon />
        <ExportIcon template="room" />
      </Toolbar>
      { showImport && <Import /> }
      { data && property && inflation && !showImport && <RoomTable tableId={tableId} data={_data} inflationData={inflation} defaults={property['defaultshospitality'] || {}} selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}} /> }
      { !showImport && <ContentSummary_s>{summaryText}</ContentSummary_s> }
    </Rooms_s>
  )
}

export default Rooms
